/**
 * Business — 商家经营分析页
 *
 * AI Agent 驱动的经营数据分析与营销决策平台
 * 功能：核心指标/营收趋势/评价分析/竞品对比/营销工具/AI建议
 *
 * 设计风格：暗夜蓝 + 萤火暖金，统一主布局风格
 */

import { useState, useEffect, useCallback } from 'react';

/* ═══════════════════════════════════════════
   工具函数
   ═══════════════════════════════════════════ */

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return '上午好';
  if (h >= 12 && h < 18) return '下午好';
  if (h >= 18 && h < 22) return '晚上好';
  return '夜深了';
};

/* ═══════════════════════════════════════════
   Mock 数据
   ═══════════════════════════════════════════ */

const KPI_DATA = [
  { label: '总营收', value: '¥128,560', change: +12.5, icon: '💰', desc: '本月累计' },
  { label: '订单量', value: '3,842', change: +8.3, icon: '📦', desc: '本月累计' },
  { label: '客单价', value: '¥33.5', change: -2.1, icon: '🎯', desc: '平均每单' },
  { label: '复购率', value: '28.6%', change: +5.7, icon: '🔄', desc: '30天内' },
];

const REVENUE_7DAYS = [
  { day: '周一', value: 15200 },
  { day: '周二', value: 18900 },
  { day: '周三', value: 16800 },
  { day: '周四', value: 21500 },
  { day: '周五', value: 24300 },
  { day: '周六', value: 28700 },
  { day: '周日', value: 19800 },
];

const REVENUE_30DAYS = Array.from({ length: 30 }, (_, i) => ({
  day: `${i + 1}日`,
  value: Math.floor(12000 + Math.random() * 18000),
}));

const POSITIVE_KEYWORDS = [
  { text: '味道好', weight: 95 },
  { text: '配送快', weight: 88 },
  { text: '分量足', weight: 82 },
  { text: '新鲜', weight: 78 },
  { text: '服务态度好', weight: 75 },
  { text: '性价比高', weight: 70 },
  { text: '包装精美', weight: 65 },
  { text: '推荐', weight: 60 },
  { text: '回头客', weight: 55 },
  { text: '干净卫生', weight: 50 },
];

const NEGATIVE_KEYWORDS = [
  { text: '等太久', weight: 72 },
  { text: '偏咸', weight: 58 },
  { text: '包装破损', weight: 45 },
  { text: '漏餐', weight: 40 },
  { text: '价格贵', weight: 35 },
  { text: '配送慢', weight: 30 },
];

const COMPETITOR_DATA = [
  { name: '老王家烧烤', rating: 4.8, price: '¥25-60', advantage: '口碑好/老客多', 劣势: '品类单一' },
  { name: '小李炸鸡', rating: 4.6, price: '¥18-45', advantage: '出餐快/性价比高', 劣势: '口味一般' },
  { name: '张记火锅', rating: 4.9, price: '¥50-120', advantage: '食材高端/环境好', 劣势: '价格偏高' },
  { name: '陈姐小吃', rating: 4.3, price: '¥10-30', advantage: '价格低/覆盖广', 劣势: '品质不稳定' },
];

const AI_SUGGESTIONS = [
  {
    title: '优化午市套餐',
    content: '数据显示午间订单占比仅18%，建议推出15-25元午市快套餐，预计可提升午间营收35%。',
    priority: 'high',
  },
  {
    title: '提升复购率',
    content: '30天复购率28.6%低于行业均值，建议对首单用户推送7日内二次优惠券，目标提升至40%。',
    priority: 'medium',
  },
  {
    title: '差评预警处理',
    content: '"等太久"提及率上升12%，建议优化高峰期出餐流程或调整配送范围承诺。',
    priority: 'high',
  },
  {
    title: '拓展品类',
    content: '竞品分析显示烧烤+饮品组合更受欢迎，建议增加饮品线，预计客单价可提升8-12元。',
    priority: 'low',
  },
];

const MARKETING_TEMPLATES = [
  '🔥 招牌秘制烤串，精选食材现烤现卖！满30减5，新客立减8元，快来尝鲜～',
  '✨ 限时特惠！双人套餐仅需58元，含招牌烤串+饮品，周末聚餐首选！',
  '🎉 老客回馈季！集满3张积分卡送招牌菜一份，感谢一路有你～',
  '🌟 新品上市！蒜蓉生蚝尝鲜价9.9元/只，每日限量50份，手慢无！',
];

const AGENT_STATUS_LIST = [
  { id: 'data-collector', name: '数据采集', icon: '📡', color: '#6366f1' },
  { id: 'trend-analyst', name: '趋势分析', icon: '📊', color: '#8b5cf6' },
  { id: 'review-miner', name: '评价挖掘', icon: '🔍', color: '#a78bfa' },
  { id: 'competitor-tracker', name: '竞品追踪', icon: '🎯', color: '#c4b5fd' },
  { id: 'strategy-gen', name: '策略生成', icon: '💡', color: '#e8d28a' },
];

/* ═══════════════════════════════════════════
   子组件
   ═══════════════════════════════════════════ */

/** KPI 指标卡片 */
function KpiCard({ data, index }: { data: typeof KPI_DATA[0]; index: number }) {
  const isPositive = data.change >= 0;
  return (
    <div
      style={{
        padding: '20px',
        borderRadius: 16,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        transition: 'all 250ms var(--ease)',
        animation: `fadeUp 0.5s ${(0.1 + index * 0.08).toFixed(2)}s var(--ease) both`,
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.15)';
        e.currentTarget.style.background = 'rgba(232, 210, 138, 0.03)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 22 }}>{data.icon}</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 8,
            background: isPositive ? 'rgba(74, 222, 128, 0.1)' : 'rgba(248, 113, 113, 0.1)',
            color: isPositive ? '#4ade80' : '#f87171',
          }}
        >
          {isPositive ? '↑' : '↓'} {Math.abs(data.change)}%
        </span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4, letterSpacing: '-0.02em' }}>
        {data.value}
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 2 }}>
        {data.label}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {data.desc}
      </div>
    </div>
  );
}

/** 简易柱状图 */
function BarChart({ data, period }: { data: typeof REVENUE_7DAYS; period: string }) {
  const maxVal = Math.max(...data.map(d => d.value));
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: period === '7天' ? 12 : 4,
        height: 180,
        padding: '20px 0 8px',
      }}
    >
      {data.map((item, idx) => {
        const height = (item.value / maxVal) * 140;
        return (
          <div
            key={idx}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: period === '7天' ? 40 : 20,
                height: height,
                borderRadius: period === '7天' ? '6px 6px 0 0' : '3px 3px 0 0',
                background: `linear-gradient(180deg, rgba(232, 210, 138, 0.8) 0%, rgba(232, 210, 138, 0.2) 100%)`,
                transition: 'all 300ms var(--ease)',
                animation: `fadeUp 0.5s ${(0.15 + idx * 0.04).toFixed(2)}s var(--ease) both`,
                cursor: 'pointer',
                position: 'relative',
              }}
              title={`${item.day}: ¥${item.value.toLocaleString()}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(180deg, rgba(232, 210, 138, 1) 0%, rgba(232, 210, 138, 0.4) 100%)';
                e.currentTarget.style.transform = 'scaleY(1.05)';
                e.currentTarget.style.transformOrigin = 'bottom';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(180deg, rgba(232, 210, 138, 0.8) 0%, rgba(232, 210, 138, 0.2) 100%)';
                e.currentTarget.style.transform = 'scaleY(1)';
              }}
            />
            {period === '7天' && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {item.day}
              </span>
            )}
            {period === '30天' && idx % 5 === 0 && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {item.day}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** 关键词标签云 */
function KeywordTag({ text, weight, type }: { text: string; weight: number; type: 'positive' | 'negative' }) {
  const fontSize = 11 + (weight / 100) * 8;
  const opacity = 0.5 + (weight / 100) * 0.5;
  const bgColor = type === 'positive'
    ? `rgba(74, 222, 128, ${0.05 + (weight / 100) * 0.1})`
    : `rgba(248, 113, 113, ${0.05 + (weight / 100) * 0.1})`;
  const borderColor = type === 'positive'
    ? `rgba(74, 222, 128, ${0.1 + (weight / 100) * 0.2})`
    : `rgba(248, 113, 113, ${0.1 + (weight / 100) * 0.2})`;
  const textColor = type === 'positive' ? '#4ade80' : '#f87171';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '5px 12px',
        borderRadius: 20,
        background: bgColor,
        border: `1px solid ${borderColor}`,
        fontSize,
        color: textColor,
        fontWeight: 500,
        opacity,
        transition: 'all 200ms var(--ease)',
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.08)';
        e.currentTarget.style.opacity = '1';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.opacity = String(opacity);
      }}
    >
      {text}
    </span>
  );
}

/** Agent 工作状态指示器 */
function AgentStatusItem({ agent, status }: { agent: typeof AGENT_STATUS_LIST[0]; status: 'idle' | 'running' | 'done' }) {
  const statusColor = status === 'done' ? '#4ade80' : status === 'running' ? agent.color : 'rgba(255,255,255,0.15)';
  const statusIcon = status === 'done' ? '✓' : status === 'running' ? '⚡' : '○';

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', borderRadius: 12,
        background: status === 'running' ? `${agent.color}08` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${status === 'running' ? `${agent.color}25` : 'rgba(255,255,255,0.05)'}`,
        transition: 'all 300ms var(--ease)',
        animation: status === 'running' ? 'gentlePulse 1.5s ease-in-out infinite' : 'none',
      }}
    >
      <span style={{ fontSize: 18 }}>{agent.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: status === 'running' ? agent.color : 'var(--text-secondary)', fontWeight: 500 }}>
          {agent.name}
        </div>
        {status === 'running' && (
          <div style={{ marginTop: 4, height: 2, borderRadius: 1, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: '60%',
                borderRadius: 1,
                background: agent.color,
                animation: 'shimmer 1.2s ease-in-out infinite',
              }}
            />
          </div>
        )}
      </div>
      <span style={{ fontSize: 14, color: statusColor, fontWeight: 600, minWidth: 16, textAlign: 'center' }}>{statusIcon}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════
   主组件
   ═══════════════════════════════════════════ */

export default function Business() {
  const [chartPeriod, setChartPeriod] = useState<'7天' | '30天'>('7天');
  const [marketingText, setMarketingText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [agentStates, setAgentStates] = useState<Record<string, 'idle' | 'running' | 'done'>>({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 模拟 Agent 工作进度
  useEffect(() => {
    const states: Record<string, 'idle' | 'running' | 'done'> = {};
    AGENT_STATUS_LIST.forEach(a => { states[a.id] = 'idle'; });
    setAgentStates(states);

    const timeline: { id: string; action: 'running' | 'done'; delay: number }[] = [];
    let delay = 300;
    AGENT_STATUS_LIST.forEach(a => {
      timeline.push({ id: a.id, action: 'running', delay });
      timeline.push({ id: a.id, action: 'done', delay: delay + 800 + Math.random() * 600 });
      delay += 1000 + Math.random() * 500;
    });

    const timers = timeline.map(t =>
      setTimeout(() => {
        setAgentStates(prev => ({ ...prev, [t.id]: t.action }));
      }, t.delay)
    );

    return () => timers.forEach(clearTimeout);
  }, []);

  const handleGenerateMarketing = useCallback(() => {
    setIsGenerating(true);
    setMarketingText('');
    setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * MARKETING_TEMPLATES.length);
      setMarketingText(MARKETING_TEMPLATES[randomIndex]);
      setIsGenerating(false);
    }, 1200);
  }, []);

  const currentRevenueData = chartPeriod === '7天' ? REVENUE_7DAYS : REVENUE_30DAYS;

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '28px 24px 60px',
        fontFamily: 'var(--font-sans)',
        color: 'var(--text-primary)',
      }}
    >
      {/* ── 页面标题区 ── */}
      <header
        style={{
          maxWidth: 960, margin: '0 auto 32px',
          animation: 'fadeUp 0.6s var(--ease) both',
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: 8 }}>
          {getTimeOfDay()} · 商家经营分析
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 300, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
          商家经营分析
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
          AI Agent 实时分析经营数据，从营收趋势到竞品对比，为你的店铺提供全方位决策支持
        </p>
      </header>

      {/* ── 主内容区 ── */}
      <div style={{ maxWidth: 960, margin: '0 auto' }}>

        {/* ── 核心指标卡片 ── */}
        <section style={{ animation: 'fadeUp 0.6s 0.05s var(--ease) both' }}>
          <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            核心指标
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 14,
            }}
          >
            {KPI_DATA.map((kpi, idx) => (
              <KpiCard key={kpi.label} data={kpi} index={idx} />
            ))}
          </div>
        </section>

        {/* ── 营收趋势图表 ── */}
        <section style={{ marginTop: 36, animation: 'fadeUp 0.6s 0.15s var(--ease) both' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: 0 }}>
              营收趋势
            </h2>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['7天', '30天'] as const).map(period => (
                <button
                  key={period}
                  onClick={() => setChartPeriod(period)}
                  style={{
                    padding: '5px 14px', borderRadius: 20,
                    background: chartPeriod === period ? 'var(--accent-soft)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${chartPeriod === period ? 'rgba(232, 210, 138, 0.2)' : 'rgba(255,255,255,0.05)'}`,
                    color: chartPeriod === period ? 'var(--accent)' : 'var(--text-muted)',
                    fontSize: 12, cursor: 'pointer',
                    fontWeight: chartPeriod === period ? 500 : 400,
                    transition: 'all 200ms',
                  }}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          <div
            style={{
              padding: '24px',
              borderRadius: 16,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <BarChart data={currentRevenueData} period={chartPeriod} />
          </div>
        </section>

        {/* ── 评价分析 + 竞品分析 两栏布局 ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 20,
            marginTop: 36,
          }}
        >
          {/* 评价分析 */}
          <section style={{ animation: 'fadeUp 0.6s 0.25s var(--ease) both' }}>
            <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
              评价分析
            </h2>
            <div
              style={{
                padding: '20px',
                borderRadius: 16,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#4ade80', fontWeight: 500, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                  正面评价
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {POSITIVE_KEYWORDS.map(kw => (
                    <KeywordTag key={kw.text} text={kw.text} weight={kw.weight} type="positive" />
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#f87171', fontWeight: 500, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#f87171', display: 'inline-block' }} />
                  负面评价
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {NEGATIVE_KEYWORDS.map(kw => (
                    <KeywordTag key={kw.text} text={kw.text} weight={kw.weight} type="negative" />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 竞品分析 */}
          <section style={{ animation: 'fadeUp 0.6s 0.3s var(--ease) both' }}>
            <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
              竞品分析
            </h2>
            <div
              style={{
                borderRadius: 16,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <th style={thStyle}>竞对</th>
                    <th style={thStyle}>评分</th>
                    <th style={thStyle}>价格</th>
                    <th style={thStyle}>优势</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPETITOR_DATA.map((comp, idx) => (
                    <tr
                      key={comp.name}
                      style={{
                        borderBottom: idx < COMPETITOR_DATA.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                        transition: 'background 200ms',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{comp.name}</td>
                      <td style={tdStyle}>
                        <span style={{ color: '#e8d28a' }}>★</span> {comp.rating}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12 }}>{comp.price}</td>
                      <td style={{ ...tdStyle, fontSize: 12, color: 'var(--text-muted)' }}>{comp.advantage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* ── 营销策划工具 ── */}
        <section style={{ marginTop: 36, animation: 'fadeUp 0.6s 0.35s var(--ease) both' }}>
          <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            营销策划工具
          </h2>
          <div
            style={{
              padding: '24px',
              borderRadius: 16,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <button
                onClick={handleGenerateMarketing}
                disabled={isGenerating}
                style={{
                  padding: '10px 20px', borderRadius: 12,
                  background: isGenerating ? 'rgba(232, 210, 138, 0.1)' : 'linear-gradient(135deg, rgba(232, 210, 138, 0.15) 0%, rgba(232, 210, 138, 0.08) 100%)',
                  border: '1px solid rgba(232, 210, 138, 0.2)',
                  color: 'var(--accent)',
                  fontSize: 13, fontWeight: 500,
                  cursor: isGenerating ? 'default' : 'pointer',
                  transition: 'all 200ms',
                  opacity: isGenerating ? 0.6 : 1,
                }}
              >
                {isGenerating ? '生成中...' : '✨ 生成营销文案'}
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                基于当前经营数据智能生成
              </span>
            </div>
            {marketingText && (
              <div
                style={{
                  padding: '16px 20px',
                  borderRadius: 12,
                  background: 'rgba(232, 210, 138, 0.04)',
                  border: '1px solid rgba(232, 210, 138, 0.1)',
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: 'var(--text-primary)',
                  animation: 'fadeUp 0.3s var(--ease) both',
                }}
              >
                {marketingText}
              </div>
            )}
            {!marketingText && !isGenerating && (
              <div
                style={{
                  padding: '20px',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.01)',
                  border: '1px dashed rgba(255,255,255,0.06)',
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'var(--text-muted)',
                }}
              >
                点击上方按钮，AI 将基于你的经营数据生成营销文案
              </div>
            )}
          </div>
        </section>

        {/* ── AI 建议卡片 ── */}
        <section style={{ marginTop: 36, animation: 'fadeUp 0.6s 0.4s var(--ease) both' }}>
          <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            AI 经营建议
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            {AI_SUGGESTIONS.map((suggestion, idx) => {
              const priorityColor = suggestion.priority === 'high' ? '#f87171' : suggestion.priority === 'medium' ? '#e8d28a' : '#a8c7d8';
              const priorityLabel = suggestion.priority === 'high' ? '高优' : suggestion.priority === 'medium' ? '中优' : '低优';
              return (
                <div
                  key={idx}
                  style={{
                    padding: '18px',
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    transition: 'all 250ms var(--ease)',
                    animation: `fadeUp 0.5s ${(0.45 + idx * 0.06).toFixed(2)}s var(--ease) both`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.12)';
                    e.currentTarget.style.background = 'rgba(232, 210, 138, 0.02)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                      {suggestion.title}
                    </span>
                    <span
                      style={{
                        fontSize: 10, fontWeight: 600,
                        padding: '2px 8px', borderRadius: 8,
                        background: `${priorityColor}15`,
                        color: priorityColor,
                      }}
                    >
                      {priorityLabel}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>
                    {suggestion.content}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Agent 工作状态 ── */}
        <section style={{ marginTop: 48, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.05)', animation: 'fadeUp 0.6s 0.5s var(--ease) both' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', animation: 'gentlePulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>
              Agent 工作状态
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 10,
            }}
          >
            {AGENT_STATUS_LIST.map(agent => (
              <AgentStatusItem
                key={agent.id}
                agent={agent}
                status={agentStates[agent.id] || 'idle'}
              />
            ))}
          </div>
        </section>
      </div>

      {/* ── 全局样式（动画） ── */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gentlePulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════
   表格样式常量
   ═══════════════════════════════════════════ */

const thStyle: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 13,
  color: 'var(--text-secondary)',
  verticalAlign: 'top',
};
